/**
 * Spend-line import wizard shapes (IB-184) — mirrors the backend contract exactly
 * (backend/.../module/film/api/dto/SpendImport*.java). Kept in its own file, deliberately
 * separate from `types/film.ts`, mirroring `api/spendImport.ts`'s own separation.
 */

export interface SpendImportBatchView {
  id: string
  productionId: string
  fiscalYearId: string
  filename: string
  rowCount: number
  headers: string[]
  /** Canonical field -> CSV header, best-effort alias match — never authoritative. */
  suggestedMapping: Record<string, string>
  sampleRows: Record<string, string>[]
  status: 'uploaded' | 'committed' | string
  createdAt: string
}

export interface SpendImportViolation {
  field: string
  code: string
  message?: string | null
}

export interface SpendImportRowPreview {
  rowNumber: number
  status: 'valid' | 'warning' | 'error' | string
  violations: SpendImportViolation[]
  mappedFields: Record<string, unknown>
}

export interface SpendImportPreviewView {
  batchId: string
  totalRows: number
  validCount: number
  warningCount: number
  errorCount: number
  rows: SpendImportRowPreview[]
}

export interface SpendImportRowOutcome {
  rowNumber: number
  outcome: 'created' | 'skipped' | string
  spendLineId: string | null
  violations: SpendImportViolation[]
}

export interface SpendImportCommitResultView {
  batchId: string
  status: string
  createdCount: number
  skippedCount: number
  adjustedCount: number
  committedAt: string
  rows: SpendImportRowOutcome[]
}

/** Canonical field names the column-map step lets the caller assign a CSV header to. */
export const SPEND_IMPORT_CANONICAL_FIELDS = [
  'category',
  'amount',
  'currency',
  'phaseRef',
  'vendorId',
  'personnelId',
  'evidenceRef',
  'qualifyingPctOverride',
  'comment',
] as const

export type SpendImportCanonicalField = (typeof SPEND_IMPORT_CANONICAL_FIELDS)[number]
